import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Send,
  X,
  Search,
  Mail,
  Building2,
  Eye,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Users,
  ChevronRight,
  Info,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/toaster.jsx';
import { useAuth } from '@/contexts/AuthContext.jsx';
import { fetchSuppliers } from '@/pages/parametrizacion/suppliers/Services/suppliersService';
import { sendLclProviderInvitations } from '../Services/lclProviderInvitationsService';
import { cn } from '@/lib/utils';

/**
 * Modal para invitar proveedores a cotizar una solicitud LCL.
 *
 * Gemelo de `pages/air-requests/components/ProviderInvitationModal.jsx`,
 * con los datos que un proveedor marítimo necesita para cotizar:
 * puertos (UN/LOCODE), volumen en m³ y unidades W/M en vez de peso
 * volumétrico, más el alcance del servicio (puerto/puerta).
 *
 * - Lista los proveedores activos del tenant.
 * - Permite seleccionar varios.
 * - Muestra preview del email que enviará el backend.
 * - Reply-To = email del usuario logueado.
 */
const LclProviderInvitationModal = ({
  open,
  onOpenChange,
  lclRequest,
  onSent,
}) => {
  const toast = useToast();
  const { user, tenant } = useAuth();

  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(true);

  // Cargar proveedores al abrir
  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setSearch('');
    setShowPreview(true);
    const load = async () => {
      setLoadingSuppliers(true);
      try {
        const r = await fetchSuppliers({
          limit: 200,
          query: { estado: 'Activo' },
          sort: { nombre: 1 },
        });
        const list = r?.data || r?.results || r || [];
        setSuppliers(Array.isArray(list) ? list : []);
      } catch (e) {
        toast.error('No se pudieron cargar los proveedores', e?.message);
      } finally {
        setLoadingSuppliers(false);
      }
    };
    load();
  }, [open, toast]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return suppliers;
    return suppliers.filter((s) => {
      return [
        s.nombre,
        s.nombreComercial,
        s.correo,
        s.contacto,
        s.ciudad,
        s.pais,
        ...(s.contactos || []).map((c) => `${c.nombre} ${c.correo}`),
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [suppliers, search]);

  const toggle = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelected(new Set(filtered.map((s) => s._id)));
  }, [filtered]);

  const clearAll = useCallback(() => setSelected(new Set()), []);

  const selectedSuppliers = useMemo(
    () => suppliers.filter((s) => selected.has(s._id)),
    [suppliers, selected],
  );

  // =============== Enviar ===============
  const handleSend = async () => {
    if (selected.size === 0) {
      toast.info('Seleccioná al menos un proveedor');
      return;
    }
    setSubmitting(true);
    try {
      const r = await sendLclProviderInvitations({
        lclRequestId: lclRequest._id,
        providerIds: Array.from(selected),
        replyTo: user?.correo || user?.email,
        notes: undefined,
      });
      const sent = r?.sent ?? 0;
      const total = r?.totalProviders ?? selected.size;
      const mode = r?.mode;
      const skipped = r?.results?.filter((x) => !x.sent && x.error) || [];
      if (mode === 'dry-run') {
        toast.success(
          `[Dry-run] ${sent} invitación${sent === 1 ? '' : 'es'} lista${sent === 1 ? '' : 's'}`,
          'No se envió ningún email real (modo desarrollo). Revisá la consola del backend.',
        );
      } else {
        toast.success(
          `Invitaciones enviadas: ${sent}/${total}`,
          skipped.length
            ? `${skipped.length} omitidos (sin email)`
            : undefined,
        );
      }
      onSent?.(r);
      onOpenChange(false);
    } catch (e) {
      toast.error('No se pudieron enviar las invitaciones', e?.message);
    } finally {
      setSubmitting(false);
    }
  };

  // =============== Preview ===============
  const previewEmail = useMemo(
    () => buildPreviewEmail(lclRequest, tenant, user),
    [lclRequest, tenant, user],
  );

  const userEmail = user?.correo || user?.email;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[90vw] max-w-none max-h-[90vh] p-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="bg-amber-100 text-amber-700 p-2 rounded-lg shrink-0">
              <Send className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-lg">Invitar proveedores a cotizar</DialogTitle>
              <DialogDescription>
                Solicitud <code className="font-mono">{lclRequest?.codigo || '—'}</code> ·
                {' '}{userEmail && <>respuestas a <strong>{userEmail}</strong></>}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 min-h-0">
          {/* ====== Lista de proveedores ====== */}
          <div className="flex flex-col border-r min-h-0">
            <div className="p-3 border-b space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Buscar proveedor por nombre, email, ciudad…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {selected.size} de {filtered.length} seleccionados
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={selectAll}
                    disabled={filtered.length === 0}
                  >
                    Seleccionar visibles
                  </button>
                  <span>·</span>
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={clearAll}
                    disabled={selected.size === 0}
                  >
                    Limpiar
                  </button>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-2 space-y-1 custom-scrollbar">
              {loadingSuppliers ? (
                <div className="flex items-center justify-center py-12 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Cargando proveedores…
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center text-center text-muted-foreground py-12">
                  <Building2 className="h-8 w-8 mb-2 opacity-50" />
                  <p className="text-sm">No hay proveedores activos</p>
                  <p className="text-xs mt-1">
                    Cargá proveedores en Parametrizaciones &rarr; Proveedores.
                  </p>
                </div>
              ) : (
                filtered.map((s) => {
                  const isSelected = selected.has(s._id);
                  const email = s.correo || (s.contactos || []).find((c) => c.correo)?.correo;
                  return (
                    <button
                      type="button"
                      key={s._id}
                      onClick={() => toggle(s._id)}
                      className={cn(
                        'w-full text-left rounded-md border p-3 transition-colors flex items-start gap-3',
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:bg-muted/40',
                      )}
                    >
                      <div
                        className={cn(
                          'mt-0.5 h-4 w-4 rounded border-2 flex items-center justify-center shrink-0',
                          isSelected
                            ? 'bg-primary border-primary'
                            : 'border-input',
                        )}
                      >
                        {isSelected && <CheckCircle2 className="h-3 w-3 text-primary-foreground" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium truncate">
                            {s.nombreComercial || s.nombre}
                          </span>
                          {s.ciudad && (
                            <Badge variant="outline" className="font-normal text-[10px] py-0">
                              {s.ciudad}
                            </Badge>
                          )}
                        </div>
                        {email ? (
                          <p className="text-xs text-muted-foreground truncate mt-0.5">
                            <Mail className="inline h-3 w-3 mr-1" />
                            {email}
                          </p>
                        ) : (
                          <p className="text-xs text-rose-600 mt-0.5 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            Sin email — no se va a poder invitar
                          </p>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* ====== Preview del email ====== */}
          <div className="flex flex-col bg-muted/20 min-h-0">
            <div className="p-3 border-b flex items-center justify-between bg-card">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Eye className="h-4 w-4 text-muted-foreground" />
                Preview del email
              </div>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setShowPreview((v) => !v)}
                className="h-7 text-xs"
              >
                {showPreview ? 'Ocultar' : 'Mostrar'}
              </Button>
            </div>

            {showPreview && (
              <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                {/* Headers */}
                <div className="rounded-md border bg-card p-3 space-y-1 text-xs font-mono">
                  <div>
                    <span className="text-muted-foreground">From:</span>{' '}
                    {tenant?.nombreComercial || tenant?.nombre || 'OptyCargo'} &lt;info@optycargo.com&gt;
                  </div>
                  {selectedSuppliers.length > 0 && (
                    <div>
                      <span className="text-muted-foreground">To:</span>{' '}
                      {selectedSuppliers
                        .map((s) => s.correo || (s.contactos || []).find((c) => c.correo)?.correo || '—')
                        .filter(Boolean)
                        .join(', ') || '— (seleccioná al menos uno)'}
                    </div>
                  )}
                  {userEmail && (
                    <div>
                      <span className="text-muted-foreground">Reply-To:</span>{' '}
                      <span className="text-primary font-semibold">{userEmail}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Subject:</span> {previewEmail.subject}
                  </div>
                </div>

                {/* Body (texto plano) */}
                <div className="rounded-md border bg-card p-4 text-sm">
                  <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed text-foreground">
{previewEmail.body}
                  </pre>
                </div>

                <div className="flex items-start gap-2 text-xs text-muted-foreground bg-blue-50 border border-blue-200 rounded-md p-2">
                  <Info className="h-3.5 w-3.5 text-blue-600 mt-0.5 shrink-0" />
                  <span>
                    El HTML se renderiza con el layout de OptyCargo (header con tu marca + footer).
                    Lo que ves arriba es el <strong>contenido del cuerpo</strong>.
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ====== Footer ====== */}
        <div className="px-6 py-3 border-t bg-card flex items-center justify-between gap-3">
          <div className="text-xs text-muted-foreground">
            {selected.size > 0 ? (
              <>
                {selected.size === 1 ? 'Se va a enviar' : 'Se van a enviar'}{' '}
                <strong>{selected.size}</strong>{' '}
                {selected.size === 1 ? 'invitación' : 'invitaciones'}
                {userEmail && (
                  <> · las respuestas irán a <strong>{userEmail}</strong></>
                )}
              </>
            ) : (
              'Seleccioná al menos un proveedor para enviar'
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              onClick={handleSend}
              disabled={selected.size === 0 || submitting}
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {submitting ? 'Enviando…' : `Enviar ${selected.size} invitación${selected.size === 1 ? '' : 'es'}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LclProviderInvitationModal;

// =============== Helpers ===============

/**
 * Preview del cuerpo del email tal cual lo genera el backend con
 * `providerInvitationLclTemplate`. Es solo para que el operador vea
 * que va a salir antes de enviar.
 */
const buildPreviewEmail = (lclRequest, tenant, user) => {
  const codigo = lclRequest?.codigo || '—';
  const descripcion =
    lclRequest?.descripcionResumen || lclRequest?.tipoOperacion || '—';
  const incoterm = lclRequest?.incotermSnapshot?.codigo || '—';
  const origen = formatSeaport(lclRequest?.puertoCargaSnapshot);
  const destino = formatSeaport(lclRequest?.puertoDescargaSnapshot);
  const detalles = lclRequest?.detalles || [];

  // Los totales los calcula y guarda el backend; acá solo se muestran.
  const totalPiezas = lclRequest?.numPiezas ?? 0;
  const pesoTotal = lclRequest?.pesoTotal ?? 0;
  const volumenTotal = lclRequest?.volumenTotal ?? 0;
  const unidadesWM = lclRequest?.unidadesWM ?? 0;

  const operatorName = tenant?.nombreComercial || tenant?.nombre || 'OptyCargo';
  const subject = `[${codigo}] Solicitud de cotización LCL`;

  const body = [
    operatorName,
    '',
    'Hola,',
    '',
    `${operatorName} está solicitando una cotización de carga consolidada (LCL) para la siguiente mercancía:`,
    '',
    `Descripción: ${descripcion}`,
    lclRequest?.tipoServicio ? `Servicio: ${lclRequest.tipoServicio}` : null,
    `Incoterm: ${incoterm}`,
    `Puerto de origen: ${origen}`,
    lclRequest?.ciudadOrigen ? `Recolección en: ${lclRequest.ciudadOrigen}` : null,
    `Puerto de destino: ${destino}`,
    lclRequest?.ciudadDestino ? `Entrega en: ${lclRequest.ciudadDestino}` : null,
    lclRequest?.valorMercancia
      ? `Valor declarado: ${lclRequest.valorMercancia} ${lclRequest.monedaValorMercancia || 'USD'} (aproximado)`
      : null,
    '',
    'Totales:',
    `Bultos = ${totalPiezas} | Peso = ${pesoTotal} kg | Volumen = ${volumenTotal} m³ | W/M = ${unidadesWM}`,
    '',
    'Detalle de bultos:',
    ...detalles.map((d, idx) => {
      const dims =
        d.largo && d.ancho && d.alto
          ? `${d.largo} x ${d.ancho} x ${d.alto} ${d.unidadDimension || 'cm'}`
          : '—';
      const apilable = d.apilable === 'Si' || d.apilable === true ? 'Sí' : 'No';
      const tipo =
        d.tipoMercanciaSnapshot?.nombre ||
        d.tipoMercanciaSnapshot?.descripcion ||
        'general';
      const imo = d.imo
        ? `, IMO=${d.imoClase || 'sí'}${d.unNumber ? ' ' + d.unNumber : ''}`
        : '';
      return `#${idx + 1}: ${d.numPiezas} ${d.tipoBulto || 'bulto(s)'}, Dimensiones=${dims}, Peso=${d.peso} ${d.unidadPeso || 'kg'}, Volumen=${d.volumen ?? 0} m³, Tipo=${tipo}, Apilable=${apilable}${imo}`;
    }),
    '',
    'Por favor envíanos tu mejor oferta incluyendo flete, recargos y tiempo de tránsito.',
    '',
    'Operador: ' + (user?.nombre || user?.name || 'Equipo de operaciones'),
  ]
    .filter((l) => l !== null)
    .join('\n');

  return { subject, body };
};

/** Puerto legible: "Nombre, Ciudad, País (UNLOCODE)". */
const formatSeaport = (p) => {
  if (!p) return '—';
  const base = [p.nombre, p.ciudad, p.pais].filter(Boolean).join(', ');
  return p.unlocode ? `${base} (${p.unlocode})` : base;
};

